import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart2, MessageCircle, Volume2, Clock, Users, AlertCircle
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserCoach } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { fetchUsageSummary, type UsageSummary } from '../../../lib/ai-coach';

export function AICoachUsageTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [usageData, setUsageData] = useState<UsageSummary[]>([]);
  const [totals, setTotals] = useState({ chatMessages: 0, voiceMinutes: 0, activeRespondents: 0 });

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const coach = await isUserCoach(user.id);
        if (!coach) { navigate('/tr/login'); return; }

        const { data: coachRecord } = await supabase
          .from('coaches')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (!coachRecord) { navigate('/tr/login'); return; }

        const summary = await fetchUsageSummary(coachRecord.id);
        setUsageData(summary);

        const totalChat = summary.reduce((sum, r) => sum + r.totalChatMessages, 0);
        const totalVoice = summary.reduce((sum, r) => sum + r.totalVoiceMinutes, 0);
        const active = summary.filter(r => r.totalChatMessages > 0 || r.totalVoiceMinutes > 0).length;

        setTotals({ chatMessages: totalChat, voiceMinutes: totalVoice, activeRespondents: active });
      } catch (err) {
        console.error('Error loading usage data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Kullanım verileri yükleniyor..." />;
  }

  const statCards = [
    { label: 'Toplam Sohbet Mesajları', value: totals.chatMessages, icon: MessageCircle, color: 'bg-sage-500' },
    { label: 'Toplam Sesli Dakikalar', value: totals.voiceMinutes, icon: Volume2, color: 'bg-warm-500' },
    { label: 'Aktif Danışanlar', value: totals.activeRespondents, icon: Users, color: 'bg-teal-500' },
  ];

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-teal-500 rounded-xl shadow-soft">
              <BarChart2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="freud-heading-xl text-chocolate-900">YZ Koç Kullanımı</h1>
              <p className="freud-text-sm text-chocolate-500">Danışanlarınızın kullanım durumunu takip edin</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.08 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`p-2.5 ${stat.color} rounded-xl shadow-soft`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
              </div>
              <div className="text-2xl font-semibold text-chocolate-900">{stat.value}</div>
              <div className="text-xs text-chocolate-500 mt-0.5">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="freud-card-elevated"
        >
          <div className="p-5 border-b border-cream-200">
            <h2 className="freud-heading-base text-chocolate-900">Danışana Göre Kullanım</h2>
          </div>

          {usageData.length === 0 ? (
            <div className="text-center py-12 text-chocolate-400">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Henüz kullanım verisi yok. Başlamak için danışanlarınız için YZ Koç'u etkinleştirin.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-cream-100">
                  <tr>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Danışan</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">E-posta</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Sohbet Mesajları</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Sesli Dakikalar</th>
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Son Etkinlik</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-200">
                  {usageData.map((row) => (
                    <tr key={row.respondentId} className="hover:bg-cream-50">
                      <td className="px-6 py-4">
                        <span className="font-medium text-chocolate-900">{row.respondentName}</span>
                      </td>
                      <td className="px-6 py-4 text-chocolate-600 text-sm">{row.respondentEmail}</td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <MessageCircle className="w-3.5 h-3.5 text-sage-500" />
                          <span className="text-sm font-medium text-chocolate-800">{row.totalChatMessages}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <Volume2 className="w-3.5 h-3.5 text-warm-500" />
                          <span className="text-sm font-medium text-chocolate-800">{row.totalVoiceMinutes}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {row.lastActivity ? (
                          <div className="flex items-center gap-1.5 text-chocolate-600">
                            <Clock className="w-3.5 h-3.5" />
                            <span className="text-sm">
                              {new Date(row.lastActivity).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400">--</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
